import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { LETTER_LAYOUT as layout, type LetterId } from './letters';
import { LETTER_PATHS, WORDMARK_PATH } from './vector-shapes';
import './CollapsibleWordmark.css';

type Letter = (typeof layout.letras)[number];

const letterById = (id: LetterId): Letter => layout.letras.find((letter) => letter.id === id)!;
const I = letterById('i');
const B = letterById('b');
const DELTA = letterById('delta');

/**
 * Recolhido, o Δ encosta no i com o mesmo respiro que tinha depois do b: "iΔ"
 * é a palavra sem ∫, h e b, não duas letras soltas.
 */
const COMPACT_DELTA_X = I.largura + (DELTA.x - (B.x + B.largura));
const COMPACT_WIDTH = COMPACT_DELTA_X + DELTA.largura;

/**
 * As letras que somem, e para que lado cada uma se dobra: ∫ e h se fecham na
 * direção do i, o b na direção do Δ (a borda que ele divide com o i).
 */
const FOLD_ORIGIN: Partial<Record<LetterId, 'left' | 'right'>> = {
  integral: 'right',
  h: 'right',
  b: 'left',
};

/** Recolhido, o i vai para o início e tudo acompanha ele, menos o Δ, que encosta nele. */
function collapsedShift(id: LetterId): number {
  return id === 'delta' ? COMPACT_DELTA_X - DELTA.x : -I.x;
}

/** Unidades do raster em `em`: a altura do raster é 1em, e o tamanho vem do `font-size`. */
const em = (units: number): string => `${units / layout.altura}em`;

export interface CollapsibleWordmarkProps {
  /** Altura em px; as letras mantêm a proporção do raster nos dois estados. */
  height: number;
  /** Recolhida, a marca vira "iΔ" no mesmo tamanho de letra. */
  collapsed: boolean;
  className?: string;
  label?: string;
}

/**
 * O ∫hibΔ letra a letra (os recortes do Wordmark, como no `WordmarkPlayful`, sem
 * os estilos de hover), que se recolhe para "iΔ": ∫, h e b se dobram e somem
 * enquanto i e Δ deslizam para o início. A caixa troca de largura de uma vez
 * (largura é layout e não anima); o que anima é só `transform` e `opacity` das
 * letras, e com menos movimento a troca é instantânea (CSS).
 */
export function CollapsibleWordmark({ height, collapsed, className, label = '∫hibΔ' }: CollapsibleWordmarkProps) {
  return (
    <span
      role="img"
      aria-label={label}
      className={cn('collapsible-wordmark', collapsed && 'collapsible-wordmark--collapsed', className)}
      style={{ fontSize: height, width: em(collapsed ? COMPACT_WIDTH : layout.largura) }}
    >
      {/* Expandida, a marca inteira evita composições diferentes entre navegadores;
          as letras independentes entram só durante a transição para o rail. */}
      <svg
        className="collapsible-wordmark__full"
        aria-hidden="true"
        viewBox={`0 0 ${layout.largura} ${layout.altura}`}
      >
        <path d={WORDMARK_PATH} fill="currentColor" fillRule="evenodd" />
      </svg>
      {layout.letras.map((letter) => {
        // O JSON sai do script com estes ids, na ordem da palavra.
        const id = letter.id as LetterId;
        const fold = FOLD_ORIGIN[id];
        return (
          <svg
            key={id}
            aria-hidden="true"
            data-letter={id}
            className={cn('collapsible-wordmark__letter', fold && 'collapsible-wordmark__letter--fold')}
            viewBox={`0 0 ${letter.largura} ${layout.altura}`}
            style={
              {
                left: em(letter.x),
                width: em(letter.largura),
                transformOrigin: fold,
                '--collapsed-shift': em(collapsedShift(id)),
              } as CSSProperties
            }
          >
            <path d={LETTER_PATHS[id]} fill="currentColor" fillRule="evenodd" />
          </svg>
        );
      })}
    </span>
  );
}
